import { useLayoutEffect, useRef } from 'react'

/**
 * A textarea that grows to fit its content and shrinks back when trimmed.
 *
 * No maximum height on purpose. A field that scrolls inside a modal that
 * scrolls gives long prose two nested scroll contexts, which is exactly what
 * makes a multi-paragraph reveal unreadable — so the field always shows all of
 * itself and the modal is the only thing that scrolls.
 *
 * useLayoutEffect, not useEffect: the resize must land in the same frame as the
 * keystroke, or the box visibly jumps a frame behind the text.
 */
export default function AutoTextarea({ value, onChange, minRows = 2, style = {}, ...rest }) {
  const ref = useRef(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    // Collapse first: scrollHeight only ever grows while a height is set, so
    // without this the field could never shrink after you delete a paragraph.
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value])

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      rows={minRows}
      style={{
        width: '100%',
        padding: '10px 12px',
        background: 'var(--field-bg)',
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
        color: 'var(--text-primary)',
        fontSize: '0.95rem',
        fontFamily: 'inherit',
        lineHeight: 1.55,
        boxSizing: 'border-box',
        resize: 'none',
        overflow: 'hidden',
        display: 'block',
        ...style,
      }}
      {...rest}
    />
  )
}
